<glxy-table-container
  [dataSource]="tableDataSource"
  [columns]="columns"
  [showFooter]="false"
  [border]="!config().hideBorder"
  [displayOption]="productTableViewType$ | async"
>
  <!--
   Table View, displayed for larger screens(larger than the window breakpoint) or when table is editable
   Each lineItem is displayed as a row, with each lineItem property(price, quantity, etc) shown as a separate column
   -->
  <mat-table>
    <mat-header-row *matHeaderRowDef="[]" />
    <ng-container matColumnDef="name">
      <mat-header-cell class="name-column-label" *matHeaderCellDef>
        {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.COLUMNS.ITEM' | translate }}
      </mat-header-cell>
      <mat-cell class="name-column" *matCellDef="let item">
        <div class="cell-top">
          <inventory-ui-item-label
            [item]="{
              itemId: item?.itemId,
              name: item?.name,
              editionName: item?.edition?.name,
              iconUrl: item?.iconUrl,
              subtext:
                viewOnly() && config().billingTermsConfig?.enabled && item?.billingTerm
                  ? (item?.billingTerm
                    | formatBillingTerm: item?.retailPricing?.frequency : config().billingTermsConfig?.defaultStartDate)
                  : viewOnly() &&
                      config().billingTermsConfig?.enabled &&
                      item?.retailPricing?.frequency !== Frequency.ONE_TIME
                    ? ('INVENTORY_UI.ITEM_PRICING_TABLE.AUTOMATICALLY_RENEW' | translate)
                    : '',
            }"
            [showItemId]="false"
            [showEditionName]="true"
          >
            @if (!config().hidePackageContents) {
              <inventory-ui-package-contents-popover
                *ngIf="item?.type === 'package'"
                [packageId]="item?.itemId"
                [partnerId]="config().partnerId"
                [marketId]="config().marketId"
              />
            }
            @if (
              !viewOnly() &&
              config().billingTermsConfig?.enabled &&
              (item?.retailPricing?.frequency !== Frequency.ONE_TIME || item?.billingTerm)
            ) {
              <div class="billing-term-link" (click)="publishChanges(item, 'edit-billing-term', 'link')">
                @if (item?.billingTerm) {
                  {{
                    item?.billingTerm
                      | formatBillingTerm
                        : item?.retailPricing?.frequency
                        : config().billingTermsConfig?.defaultStartDate
                  }}
                } @else {
                  {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.AUTOMATICALLY_RENEW' | translate }}
                }
              </div>
            }
          </inventory-ui-item-label>
        </div>
        <div class="cell-bottom fee label">
          <div class="item-fees label" *ngFor="let fee of item.fees">
            <ng-container *ngIf="fee.type === 'setup' || showManagementFees">
              <mat-icon class="fee-icon">subdirectory_arrow_right</mat-icon>
              <span>{{ fee.label }}</span>
            </ng-container>
          </div>
        </div>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="quantity">
      <mat-header-cell class="quantity-column-label" *matHeaderCellDef>
        {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.COLUMNS.QUANTITY' | translate }}
      </mat-header-cell>
      <mat-cell class="quantity-column" *matCellDef="let item" data-cy="item-quantity-cell">
        <div class="cell-top">
          @if (config()?.quantityEditable && item.isMultiactivatable && item.type === 'addon' && !viewOnly()) {
            <glxy-form-field>
              <input
                matInput
                dense
                type="number"
                min="1"
                [value]="item.quantity"
                (change)="onQuantityChange($event, item)"
              />
            </glxy-form-field>
          } @else {
            <span class="quantity">{{ item.quantity }}</span>
          }
        </div>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="wholesale_price">
      <mat-header-cell class="pricing-column-label" *matHeaderCellDef>
        {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.COLUMNS.WHOLESALE' | translate }}
      </mat-header-cell>
      <mat-cell class="pricing-column" *matCellDef="let item">
        <div>
          <div class="cell-top" [ngClass]="{ 'input-box': item.wholesalePricing.usesVariablePricing }">
            @if (
              viewOnly() ||
              item.wholesalePricing.usesVariablePricing !== true ||
              (item.wholesalePricing.usesVariablePricing === true && item.type === 'package')
            ) {
              <billing-ui-simple-price-display
                class="item-pricing"
                [align]="'end'"
                [prices]="item.wholesalePricing.frequencyPrices"
                [currencyCode]="item.wholesalePricing.currency"
                [contactSales]="item.wholesalePricing.contactSales"
                [discounts]="item.wholesalePricing.discounts"
                data-cy="wholesale-price-display"
              />
            } @else {
              <glxy-currency-field
                *ngFor="let frequencyPrice of item.wholesalePricing.frequencyPrices"
                [value]="frequencyPrice.price | centsToDollars"
                [currencyCode]="frequencyPrice.currency"
                [minimumValue]="'0'"
                [validators]="frequencyPrice.minimumPrice | centsToDollars | priceValidator"
                [hint]="frequencyPrice.minimumPrice | price: frequencyPrice.currency | formatWholesaleMinimumHint"
                [suffixText]="frequencyPrice.frequency | glxyFrequency"
                [maxWidth]="220"
                (change)="onPriceChange($event, item, 'wholesale', frequencyPrice.frequency)"
                [bottomSpacing]="'none'"
                data-cy="wholesale-spend"
              />
            }
          </div>
          <div class="cell-bottom">
            <ng-container *ngFor="let fee of item.fees">
              <billing-ui-simple-price-display
                class="item-fees simple-price-display"
                *ngIf="fee.wholesaleAmount > 0; else noFee"
                [align]="'end'"
                [price]="fee.wholesaleAmount"
                [currencyCode]="item.wholesalePricing.currency"
                [frequency]="fee.type === 'setup' ? Frequency.ONE_TIME : item.wholesalePricing.frequency"
              />
            </ng-container>
          </div>
        </div>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="retail_price">
      <mat-header-cell class="pricing-column-label" *matHeaderCellDef>
        {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.COLUMNS.RETAIL' | translate }}
      </mat-header-cell>
      <mat-cell class="pricing-column" *matCellDef="let item">
        <div>
          @if (viewOnly()) {
            <div class="view-only-top-cell">
              <billing-ui-simple-price-display
                class="item-pricing"
                [align]="'end'"
                [price]="item.retailPricing.price"
                [currencyCode]="item.retailPricing.currency"
                [frequency]="item.retailPricing.frequency"
              />
              @if (item.isDiscountedRetailPrice) {
                <billing-ui-simple-price-display
                  class="item-pricing crossed-out-price"
                  [align]="'end'"
                  [price]="item.defaultRetailPricing.price"
                  [currencyCode]="item.defaultRetailPricing.currency"
                  [frequency]="item.defaultRetailPricing.frequency"
                />
              }
            </div>
            <div class="view-only-fees">
              <ng-container *ngFor="let fee of item.fees">
                @if (fee.type === 'setup' || fee.showManagementFees) {
                  <billing-ui-simple-price-display
                    *ngIf="fee.retailAmount >= 0; else noFee"
                    class="item-pricing"
                    [align]="'end'"
                    [price]="fee.retailAmount"
                    [currencyCode]="item.retailPricing.currency"
                    [frequency]="fee.type === 'setup' ? Frequency.ONE_TIME : item.retailPricing.frequency"
                  />
                  <billing-ui-simple-price-display
                    *ngIf="fee.retailDefaultAmount > fee.retailAmount; else noFee"
                    class="item-pricing crossed-out-price"
                    [align]="'end'"
                    [price]="fee.retailDefaultAmount"
                    [currencyCode]="item.retailPricing.currency"
                    [frequency]="fee.type === 'setup' ? Frequency.ONE_TIME : item.retailPricing.frequency"
                  />
                }
              </ng-container>
            </div>
          } @else {
            <div class="cell-top input-box">
              <glxy-currency-field
                [value]="item.retailPricing.price | centsToDollars"
                [currencyCode]="item.retailPricing.currency"
                [minimumValue]="'0'"
                [suffixText]="showRetailFrequencySuffix ? (item.retailPricing.frequency | glxyFrequency) : undefined"
                [maxWidth]="220"
                (change)="onPriceChange($event, item, 'retail', item.retailPricing.frequency)"
                [bottomSpacing]="'none'"
                [viewOnly]="viewOnly()"
                data-cy="retail-price"
              />
              @if (item.displayOriginalPrice) {
                <div class="fake-hint">
                  <span class="original-price-hint">{{
                    'INVENTORY_UI.ITEM_PRICING_TABLE.ORIGINAL_PRICE_HINT' | translate
                  }}</span>
                  <billing-ui-simple-price-display
                    [align]="'end'"
                    [price]="item.defaultRetailPricing.price"
                    [currencyCode]="item.defaultRetailPricing.currency"
                    [frequency]="item.defaultRetailPricing.frequency"
                  />
                </div>
              }
            </div>
            <div class="cell-bottom">
              <ng-container *ngFor="let fee of item.fees">
                <glxy-currency-field
                  *ngIf="fee.retailAmount >= 0; else noFee"
                  [ngClass]="{ 'more-spacing': item.displayOriginalPrice }"
                  [value]="fee.retailAmount | centsToDollars"
                  [currencyCode]="item.retailPricing.currency"
                  [minimumValue]="'0'"
                  [maxWidth]="220"
                  (change)="onPriceChange($event, item, 'setupfee')"
                  [bottomSpacing]="'none'"
                  [viewOnly]="viewOnly()"
                  data-cy="retail-fee"
                />
                @if (
                  fee.type === 'setup' && fee.retailDefaultAmount > 0 && fee.retailDefaultAmount !== fee.retailAmount
                ) {
                  <div class="fake-hint">
                    <span class="original-price-hint">{{
                      'INVENTORY_UI.ITEM_PRICING_TABLE.ORIGINAL_PRICE_HINT' | translate
                    }}</span>
                    <billing-ui-simple-price-display
                      [align]="'end'"
                      [price]="fee.retailDefaultAmount"
                      [currencyCode]="item.retailPricing.currency"
                      [frequency]="Frequency.ONE_TIME"
                    />
                  </div>
                }
              </ng-container>
            </div>
          }
        </div>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="retail_price_total">
      <mat-header-cell class="pricing-column-label" *matHeaderCellDef>
        {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.COLUMNS.RETAIL_TOTAL' | translate }}
      </mat-header-cell>
      <mat-cell class="pricing-column" *matCellDef="let item">
        <div>
          <div class="cell-top">
            <billing-ui-simple-price-display
              class="item-pricing"
              [align]="'end'"
              [price]="item.retailPricing.total"
              [currencyCode]="item.retailPricing.currency"
              [frequency]="item.retailPricing.frequency"
            />
          </div>
          <div class="cell-bottom">
            <ng-container *ngFor="let fee of item.fees">
              <billing-ui-simple-price-display
                class="item-fees"
                *ngIf="fee.retailAmountTotal > 0; else noFee"
                [align]="'end'"
                [price]="fee.retailAmountTotal"
                [currencyCode]="item.retailPricing.currency"
                [frequency]="Frequency.ONE_TIME"
              />
            </ng-container>
          </div>
        </div>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="menu">
      <mat-header-cell class="menu-column" *matHeaderCellDef />
      <mat-cell class="menu-column" *matCellDef="let item">
        @if (!viewOnly()) {
          <div>
            <div class="cell-top menu">
              <button mat-icon-button [matMenuTriggerFor]="menu" data-cy="menu-trigger">
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #menu="matMenu">
                @if (config().billingTermsConfig?.enabled) {
                  <button class="edit-billing-terms" mat-menu-item (click)="publishChanges(item, 'edit-billing-term')">
                    {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.ACTION_MENU.EDIT_BILLING_TERMS' | translate }}
                  </button>

                  @if (config().selectionEditable && item.billingTerm && item.isMultiactivatable) {
                    <button class="add-billing-terms" mat-menu-item (click)="publishChanges(item, 'add-billing-term')">
                      {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.ACTION_MENU.ADD_BILLING_TERMS' | translate }}
                    </button>
                  }
                }
                <button mat-menu-item (click)="editRetailBillingFrequency(item)" data-cy="edit-billing-frequency">
                  {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.ACTION_MENU.EDIT_BILLING_FREQUENCY' | translate }}
                </button>
                <button
                  mat-menu-item
                  (click)="addSetupFee(item)"
                  [disabled]="item.fees | hasSetupFee"
                  data-cy="add-setup-fee"
                >
                  {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.ACTION_MENU.ADD_SETUP_FEE' | translate }}
                </button>
                <button mat-menu-item (click)="resetItemPricing(item)" data-cy="reset-to-default">
                  {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.ACTION_MENU.RESET_TO_DEFAULT' | translate }}
                </button>
                @if (config()?.selectionEditable) {
                  <button mat-menu-item (click)="onRemoveItem(item)">
                    {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.ACTION_MENU.REMOVE_ITEM' | translate }}
                  </button>
                }
              </mat-menu>
            </div>

            <ng-container *ngFor="let fee of item.fees">
              <div class="cell-bottom close" *ngIf="fee | isSetupFeeRemovable">
                <button mat-icon-button (click)="removeSetupFee(item, fee)" data-cy="remove-setup-fee">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </ng-container>
          </div>
        }
      </mat-cell>
    </ng-container>
    <mat-row *matRowDef="let row; columns: []" />
  </mat-table>

  <!--
    Grid View is displayed for smaller screens(smaller than the window breakpoint) when pricing table is view only.
     Table is a single column with each lineItem is displayed as a mat-card with price and quantity as card content
   -->
  <div class="grid-container" glxyGridMinCardWidth glxy-grid-content>
    <mat-card class="grid-card" appearance="outlined" *glxyGridCard="let item">
      <mat-card-content>
        <!-- item logo, name, and billing terms -->
        <div class="card-info-container grid-first-row">
          <div class="cell-top">
            <inventory-ui-item-label
              [item]="{
                itemId: item?.itemId,
                name: item?.name,
                editionName: item?.edition?.name,
                iconUrl: item?.iconUrl,
                subtext:
                  viewOnly() && config().billingTermsConfig?.enabled && item?.billingTerm
                    ? (item?.billingTerm
                      | formatBillingTerm
                        : item?.retailPricing?.frequency
                        : config().billingTermsConfig?.defaultStartDate)
                    : viewOnly() &&
                        config().billingTermsConfig?.enabled &&
                        item?.retailPricing?.frequency !== Frequency.ONE_TIME
                      ? ('INVENTORY_UI.ITEM_PRICING_TABLE.AUTOMATICALLY_RENEW' | translate)
                      : '',
              }"
              [showItemId]="false"
              [showEditionName]="true"
              [iconSize]="64"
            >
              @if (!config().hidePackageContents && item?.type === 'package') {
                <inventory-ui-package-contents-popover
                  [packageId]="item?.itemId"
                  [partnerId]="config().partnerId"
                  [marketId]="config().marketId"
                />
              }
              @if (
                !viewOnly() &&
                config().billingTermsConfig?.enabled &&
                (item?.retailPricing?.frequency !== Frequency.ONE_TIME || item?.billingTerm)
              ) {
                <div class="billing-term-link" (click)="publishChanges(item, 'edit-billing-term', 'link')">
                  @if (item?.billingTerm) {
                    {{
                      item?.billingTerm
                        | formatBillingTerm
                          : item?.retailPricing?.frequency
                          : config().billingTermsConfig?.defaultStartDate
                    }}
                  } @else {
                    {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.AUTOMATICALLY_RENEW' | translate }}
                  }
                </div>
              }
            </inventory-ui-item-label>
          </div>
        </div>
        <!-- item quantity -->
        <div class="card-section card-quantity">
          <span class="card-price-label"> {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.COLUMNS.QUANTITY' | translate }}: </span>
          <span class="quantity">{{ item.quantity }}</span>
        </div>
        <!-- retail price and retail total with fees -->
        <div class="card-section cost-cards">
          <div class="card-retail">
            <div class="card-price-label">
              {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.COLUMNS.RETAIL' | translate }}
            </div>
            <div>
              @if (viewOnly()) {
                <!-- retail price -->
                <div>
                  <billing-ui-simple-price-display
                    class="item-pricing"
                    [align]="'start'"
                    [price]="item.retailPricing.price"
                    [currencyCode]="item.retailPricing.currency"
                    [frequency]="item.retailPricing.frequency"
                  />
                </div>
                <!-- retail fees -->
                <div class="view-only-fees">
                  @for (fee of item.fees; track fee) {
                    @if (fee.type === 'setup' || fee.showManagementFees) {
                      @if (fee.retailAmount >= 0) {
                        <div class="card-fee-display card-fee-display-text">
                          +
                          <billing-ui-simple-price-display
                            [price]="fee.retailAmount"
                            class="card-fee-display-text"
                            [currencyCode]="item.retailPricing.currency"
                            [alwaysShowNumber]="true"
                            [frequency]="fee.type === 'setup' ? Frequency.ONE_TIME : item.retailPricing.frequency"
                          />
                          {{ fee.label }}
                        </div>
                      }
                    }
                  }
                </div>
                <!-- crossed out default retail price -->
                @if (item.isDiscountedRetailPrice) {
                  <div>
                    <billing-ui-simple-price-display
                      class="item-pricing crossed-out-price"
                      [align]="'start'"
                      [price]="item.defaultRetailPricing.price"
                      [currencyCode]="item.defaultRetailPricing.currency"
                      [frequency]="item.defaultRetailPricing.frequency"
                    />
                  </div>
                }
                <!-- crossed out default fee -->
                <div class="view-only-fees">
                  @for (fee of item.fees; track fee) {
                    @if (fee.type === 'setup' || fee.showManagementFees) {
                      @if (fee.retailDefaultAmount > fee.retailAmount) {
                        <div class="card-fee-display crossed-out-price card-fee-display-text">
                          +
                          <billing-ui-simple-price-display
                            [price]="fee.retailDefaultAmount"
                            [currencyCode]="item.retailPricing.currency"
                            [alwaysShowNumber]="true"
                            [frequency]="fee.type === 'setup' ? Frequency.ONE_TIME : item.retailPricing.frequency"
                          />
                          {{ fee.label }}
                        </div>
                      }
                    }
                  }
                </div>
              }
            </div>
          </div>
          <div class="card-retail-total">
            <div class="card-price-label">
              {{ 'INVENTORY_UI.ITEM_PRICING_TABLE.COLUMNS.RETAIL_TOTAL' | translate }}
            </div>
            <div>
              <!-- retail total -->
              <div>
                <billing-ui-simple-price-display
                  class="item-pricing"
                  [price]="item.retailPricing.total"
                  [currencyCode]="item.retailPricing.currency"
                  [frequency]="item.retailPricing.frequency"
                  [fees]="item.fees"
                />
              </div>
              <div>
                <!-- Total of retail fees -->
                @for (fee of item.fees; track fee) {
                  @if (fee.retailAmountTotal > 0) {
                    <div class="card-fee-display card-fee-display-text">
                      +
                      <billing-ui-simple-price-display
                        [price]="fee.retailAmountTotal"
                        [currencyCode]="item.retailPricing.currency"
                        [frequency]="Frequency.ONE_TIME"
                      />
                      {{ fee.label }}
                    </div>
                  }
                }
              </div>
            </div>
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
  @if (!config()?.hidePriceSummary) {
    <ng-container *ngIf="priceTotals$ | async as total">
      <ng-container *ngIf="total.initialized; else loadingTotals">
        <div class="price-summary-footer">
          <div class="totals-row">
            <span>{{ 'INVENTORY_UI.ITEM_PRICING_TABLE.FOOTER.TOTALS' | translate }}</span>
          </div>

          <div class="row price-summary-row" data-cy="price-summary-footer">
            <div class="row price-row wholesale-row">
              <div>
                <span class="price-type-text">{{
                  'INVENTORY_UI.ITEM_PRICING_TABLE.FOOTER.WHOLESALE' | translate
                }}</span>
              </div>
              <div class="price-display">
                <inventory-ui-price-summary-display [priceTotal]="wholesaleSummary" />
              </div>
            </div>

            <div class="row price-row retail-row">
              <div>
                <span class="price-type-text">{{ 'INVENTORY_UI.ITEM_PRICING_TABLE.FOOTER.RETAIL' | translate }}</span>
              </div>
              <div class="price-display">
                <inventory-ui-price-summary-display [priceTotal]="retailSummary" />
              </div>
            </div>
          </div>
        </div>
      </ng-container>
    </ng-container>
  }

  <ng-template #noFee>
    <div style="height: 20px; margin-bottom: 12px"></div>
  </ng-template>
  <ng-template #loadingTotals>
    <div class="stencil-shimmer loading-shimmer"></div>
  </ng-template>
</glxy-table-container>
